import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAuthenticationOptions } from '../services/createUserService';

/** Nothing offered and nothing decided, which is also the state before the fetch. */
export const EMPTY_AUTHENTICATION = {
  assignment: null,
  locked: false,
  provider: null,
  requireSso: false,
  options: [],
};

// Deliberately loose, and the same shape useCreateUserForm checks with: this only
// decides whether an address is worth asking the server about.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Only the keys this hook knows, each with a usable default.
 *
 * `options` in particular has to be an array whatever arrives: the step maps over
 * it to build the select, and an absent list would take the whole wizard down
 * rather than leaving one field empty.
 */
const normalize = (payload = {}) => ({
  assignment: payload.assignment ?? null,
  locked: Boolean(payload.locked),
  provider: payload.provider ?? null,
  requireSso: Boolean(payload.requireSso),
  options: Array.isArray(payload.options) ? payload.options : [],
});

/**
 * The Authentication field's options, and the invariant that the draft agrees with
 * them (PD-4684).
 *
 * The answer depends on two things the wizard collects on different steps — the
 * accounts from step 1 and the email from step 2 — so it cannot ride in
 * useFormOptions, which fetches once when the wizard opens. It is re-asked
 * whenever either moves, including when the admin goes back and changes accounts.
 *
 * Keeping the draft consistent is part of the job rather than the caller's, because
 * the two ways it can go stale both come from this fetch:
 *
 *   - A domain rule locks the field. The provider is assigned rather than chosen,
 *     so the draft is set to it and the select renders disabled.
 *   - A changed account set can withdraw the option already chosen. Leaving it would
 *     submit a provider the accounts do not offer, which the server refuses.
 *
 * @param {Array<string>} accountIds  step 1's selection
 * @param {string} email              step 2's email
 * @param {Function} onChange         called with the value the draft should hold
 * @param {string} value              what the draft holds now
 * @returns {{authentication: object, isAskable: boolean,
 *   isLoadingAuthentication: boolean}} `isAskable` is whether there is yet enough
 *   to ask about — the field has nothing to offer before an email is typed, which
 *   is a different state from the server offering nothing.
 */
const useAuthenticationOptions = ({
  accountIds = [],
  email = '',
  onChange,
  value = '',
}) => {
  const [authentication, setAuthentication] = useState(EMPTY_AUTHENTICATION);
  const [isLoading, setIsLoading] = useState(false);

  // Whether the server has actually answered for the current email and accounts.
  //
  // Distinct from "the list is empty", and the distinction is what stops the draft
  // being cleared wrongly. Before the first answer, and after a failed one, there is
  // no offer to reconcile against — only an absence of information — and treating
  // that as "nothing is on offer" would wipe a choice the admin had already made
  // just by arriving on the step or by losing the network for a moment.
  const [hasOffer, setHasOffer] = useState(false);

  // A stale response must not overwrite a newer one: the admin can retype the email
  // or change accounts faster than the answer comes back. Same guard, and for the
  // same reason, as the duplicate checks in useCreateUserForm.
  const requestId = useRef(0);

  // The array identity changes on every render of the caller, so the effect below
  // keys on the contents instead. Account ids contain no commas — they are dealer
  // ids — so joining them is unambiguous.
  const accountKey = accountIds.join(',');
  const trimmedEmail = email.trim();
  const isAskable = Boolean(accountKey) && EMAIL_PATTERN.test(trimmedEmail);

  const load = useCallback(async (params) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setIsLoading(true);

    try {
      const result = await fetchAuthenticationOptions(params);

      if (requestId.current === id) {
        setAuthentication(normalize(result));
        setHasOffer(true);
      }
    } catch {
      // A failed fetch leaves the field with nothing to offer rather than
      // half-offering a list that may be wrong. The admin cannot continue past a
      // required field with no options, which is the honest outcome: the server is
      // the only thing that knows what this user may sign in with, and guessing
      // would mean a create the server then refuses.
      if (requestId.current === id) {
        setAuthentication(EMPTY_AUTHENTICATION);
        setHasOffer(false);
      }
    } finally {
      if (requestId.current === id) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAskable) {
      // Bump the id so an answer already in flight cannot land after this reset.
      requestId.current += 1;
      setAuthentication(EMPTY_AUTHENTICATION);
      setHasOffer(false);
      setIsLoading(false);

      return;
    }

    load({ accountIds: accountKey.split(','), email: trimmedEmail });
  }, [accountKey, isAskable, load, trimmedEmail]);

  const { locked, options, provider } = authentication;

  useEffect(() => {
    // Mid-fetch the list on screen is the previous answer, so reconciling against
    // it would clear a choice the new answer may well still offer. Before any
    // answer, there is nothing to reconcile against at all.
    if (isLoading || !hasOffer) return;

    if (locked && provider && value !== provider) {
      onChange(provider);

      return;
    }

    if (!locked && value && !options.some((option) => option.value === value)) {
      onChange('');
    }
  }, [hasOffer, isLoading, locked, onChange, options, provider, value]);

  return { authentication, isAskable, isLoadingAuthentication: isLoading };
};

export default useAuthenticationOptions;
